import React from 'react'
import styled, { withTheme } from 'styled-components'
import PropTypes from 'prop-types'
import lighten from 'polished/lib/color/lighten'
import TagOld from './TagOld'
import themeDark from '../../styles/theme-dark'

const ProTag = ({ dark, theme, title }) => title !== 'none' ? (
	<Wrapper titleStr={title} overrideTheme={dark ? themeDark : theme}>
		<Inner>Pro</Inner>
	</Wrapper>
) : null

ProTag.propTypes = {
	dark: PropTypes.bool,
	theme: PropTypes.object.isRequired,
	title: PropTypes.string
}

ProTag.defaultProps = {
	dark: false,
	title: null
}

const Inner = styled.span`
	color: #FFF !important;
`

const Wrapper = styled(TagOld)`
	background-color: ${props => props.overrideTheme.primary};
	border: 1px solid ${props => props.overrideTheme.primary};
	border-radius: 2px;
	color: #FFF !important;
	font-size: 0.9em;
	padding: calc(0.1em - 1px) 0.2em;
	overflow: hidden;
	position: relative;

	${props => props.titleStr === 'gold' && `
		background: ${props.dark || props.overrideTheme.id === 'dark' ? props.overrideTheme.backgroundVeryLight : lighten(0.3, '#d8b066')};
		border: 1px solid rgb(246 196 105 / 40%);

		${Inner} {
			background-image: linear-gradient(295deg, #ffe5a2 5%, #bf841a 50%, #ffcd74 95%);
			background-clip: text;
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
		}
	`}

	${props => props.titleStr === 'rainbow' && `
		background-color: ${props.overrideTheme.backgroundVeryLight};
		border: 1px solid ${props.dark || props.theme.id === 'dark' ? '#333' : 'rgba(0, 0, 0, 0.1)'};

		${Inner} {
			background-image: linear-gradient(to left, violet, indigo, blue, green, yellow, orange, red);
			background-clip: text;
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
		}
	`}

	${props => props.titleStr === 'black' && `
		background-color: #111;
		border: 1px solid ${props.dark || props.overrideTheme.id === 'dark' ? '#333' : '#111'};
	`}

	${props => props.titleStr === 'pink' && `
		background: linear-gradient(135deg, #ffb6c1, #ffc0cb, #ffb6c1);
		border: 1px solid rgb(255, 146, 201);
		box-shadow: 0 2px 8px rgba(255, 182, 193, 0.3);

		${Inner} {
			color: ${props.theme.id === 'dark' ? '#000' : '#FFF'} !important;
			font-weight: 600;
		}
	`}

	${props => props.titleStr === 'fire' && `
		background: linear-gradient(45deg, #ff6b35, #f7931e, #ff6b35);
		border: 1px solid #ff4500;
		box-shadow: 0 0 15px rgba(255, 107, 53, 0.5);
		clip-path: inset(0 0 0 0);

		${Inner} {
			background: linear-gradient(45deg, #fff, #ffeb3b, #fff);
			background-clip: text;
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
			font-weight: 600;
		}

		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: -100%;
			width: 100%;
			height: 100%;
			background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
			animation: holographicShine 3s ease-in-out infinite;
		}

		@keyframes holographicShine {
			0% {
				left: -100%;
			}
			100% {
				left: 100%;
			}
		}
	`}

	${props => props.titleStr === 'cosmic' && `
		background: linear-gradient(135deg, #667eea, #764ba2);
		border: 1px solid #667eea;
		box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
		clip-path: inset(0 0 0 0);

		${Inner} {
			color: #fff !important;
			font-weight: 600;
			position: relative;
			z-index: 1;
		}
	`}

	${props => props.titleStr === 'diamond' && `
		background: linear-gradient(135deg, #00d4ff, #0099cc, #0066cc);
		border: 1px solid #00bfff;
		box-shadow: 0 0 20px rgba(0, 212, 255, 0.4), inset 0 0 20px rgba(0, 212, 255, 0.1);
		clip-path: inset(0 0 0 0);

		&::before {
			content: '';
			position: absolute;
			top: -50%;
			left: -50%;
			width: 200%;
			height: 200%;
			background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%);
			animation: diamondRotate 4s linear infinite;
		}

		&::after {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: linear-gradient(45deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%);
			animation: diamondShine 2s ease-in-out infinite;
		}

		${Inner} {
			color: #fff !important;
			font-weight: 700;
			position: relative;
			z-index: 2;
			text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
		}

		@keyframes diamondRotate {
			0% { transform: rotate(0deg); }
			100% { transform: rotate(360deg); }
		}

		@keyframes diamondShine {
			0%, 100% { opacity: 0; }
			50% { opacity: 1; }
		}
	`}

	${props => props.titleStr === 'nature' && `
		background: linear-gradient(135deg, #2d5016, #4a7c59, #2d5016);
		border: 1px solid #3a5f2b;
		box-shadow: 0 2px 8px rgba(45, 80, 22, 0.3);
		clip-path: inset(0 0 0 0);

		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background-image:
				radial-gradient(ellipse 8px 12px at 25% 30%, rgba(255,255,255,0.15) 0%, transparent 50%),
				radial-gradient(ellipse 6px 10px at 75% 70%, rgba(255,255,255,0.12) 0%, transparent 50%),
				radial-gradient(ellipse 4px 8px at 50% 20%, rgba(255,255,255,0.1) 0%, transparent 50%);
			background-size: 100% 100%;
			opacity: 0.8;
		}

		&::after {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background-image:
				radial-gradient(ellipse 2px 3px at 10% 40%, rgba(255,255,255,0.4) 0%, transparent 50%),
				radial-gradient(ellipse 1px 2px at 30% 60%, rgba(255,255,255,0.3) 0%, transparent 50%),
				radial-gradient(ellipse 1.5px 2.5px at 50% 30%, rgba(255,255,255,0.35) 0%, transparent 50%),
				radial-gradient(ellipse 1px 1.5px at 70% 50%, rgba(255,255,255,0.25) 0%, transparent 50%),
				radial-gradient(ellipse 2px 2.5px at 90% 20%, rgba(255,255,255,0.3) 0%, transparent 50%);
			background-size: 100% 100%;
			animation: windFlow 3s ease-in-out infinite;
			opacity: 0.6;
		}

		${Inner} {
			color: #fff !important;
			font-weight: 600;
			position: relative;
			z-index: 2;
			text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
		}

		@keyframes windFlow {
			0% {
				transform: translateX(-100%) translateY(0);
				opacity: 0;
			}
			20% {
				opacity: 0.6;
			}
			80% {
				opacity: 0.6;
			}
			100% {
				transform: translateX(100%) translateY(-10px);
				opacity: 0;
			}
		}
	`}

	${props => props.titleStr === 'galaxy' && `
		background: linear-gradient(135deg, #1a0033, #4a0066, #1a0033);
		border: 1px solid #660099;
		box-shadow: 0 0 25px rgba(102, 0, 153, 0.6), inset 0 0 20px rgba(153, 51, 204, 0.2);
		clip-path: inset(0 0 0 0);

		&::after {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background-image:
				radial-gradient(ellipse 2px 3px at 10% 40%, rgba(255, 255, 255, 0.9) 0%, transparent 50%),
				radial-gradient(ellipse 1px 2px at 30% 60%, rgba(255, 255, 255, 0.8) 0%, transparent 50%),
				radial-gradient(ellipse 1.5px 2.5px at 50% 30%, rgba(255, 255, 255, 0.7) 0%, transparent 50%),
				radial-gradient(ellipse 1px 1.5px at 70% 50%, rgba(255, 255, 255, 0.6) 0%, transparent 50%),
				radial-gradient(ellipse 2px 2.5px at 90% 20%, rgba(255, 255, 255, 0.5) 0%, transparent 50%);
			background-size: 100% 100%;
			animation: galaxyNebula 6s ease-in-out infinite;
		}

		${Inner} {
			background: linear-gradient(45deg, #fff, #e6ccff, #fff);
			background-clip: text;
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
			font-weight: 700;
			position: relative;
			z-index: 2;
			text-shadow: 0 0 10px rgba(230, 204, 255, 0.5), 0 0 15px rgba(153, 51, 204, 0.3);
			animation: galaxyText 3s ease-in-out infinite;
		}

		@keyframes galaxyNebula {
			0% {
				transform: rotate(0deg);
				opacity: 0;
			}
			20% {
				opacity: 0.6;
			}
			80% {
				opacity: 0.6;
			}
			100% {
				transform: rotate(360deg);
				opacity: 0;
			}
		}

		@keyframes galaxyText {
			0%, 100% {
				text-shadow: 0 0 10px rgba(230, 204, 255, 0.5), 0 0 15px rgba(153, 51, 204, 0.3);
			}
			50% {
				text-shadow: 0 0 15px rgba(230, 204, 255, 0.8), 0 0 20px rgba(153, 51, 204, 0.5), 0 0 25px rgba(255, 255, 255, 0.2);
			}
		}
	`}

	${props => props.titleStr === 'conqueror' && `
		background: linear-gradient(135deg, #8b0000, #dc143c, #8b0000);
		border: 1px solid #b22222;
		box-shadow: 0 0 20px rgba(139, 0, 0, 0.6), inset 0 0 15px rgba(220, 20, 60, 0.2);
		clip-path: inset(0 0 0 0);

		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: -100%;
			width: 100%;
			height: 100%;
			background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
			animation: holographicShine 3s ease-in-out infinite;
		}

		@keyframes holographicShine {
			0% {
				left: -100%;
			}
			100% {
				left: 100%;
			}
		}

		&::after {
			content: '';
			position: absolute;
			top: -50%;
			left: -50%;
			width: 200%;
			height: 200%;
			background: radial-gradient(circle, rgba(255, 215, 0, 0.1) 0%, transparent 70%);
			animation: conquerorPulse 3s ease-in-out infinite;
		}

		${Inner} {
			background: linear-gradient(45deg, #fff, #ffd700, #fff);
			background-clip: text;
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
			font-weight: 700;
			position: relative;
			z-index: 2;
			text-shadow: 0 0 8px rgba(255, 215, 0, 0.3);
			animation: conquerorText 2s ease-in-out infinite;
		}

		@keyframes conquerorPulse {
			0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.3; }
			50% { transform: scale(1.1) rotate(180deg); opacity: 0.6; }
		}

		@keyframes conquerorText {
			0%, 100% { transform: scale(1); }
			50% { transform: scale(1.05); }
		}
	`}
`

const enhance = withTheme

export default enhance(ProTag)
